import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import axios from "axios";

function PasswordChangeSection({ t }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setError(t("passwordChange.passwordsDoNotMatch"));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post("/api/users/password/change/", {
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      setSuccess(true);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) {
        setError(data.errors.join(" "));
      } else if (data?.error) {
        setError(data.error);
      } else {
        setError(t("passwordChange.genericError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = oldPassword && newPassword && confirmPassword && !submitting;

  return (
    <>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <LockIcon fontSize="small" />
        {t("passwordChange.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("passwordChange.success")}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          type="password"
          size="small"
          fullWidth
          label={t("passwordChange.oldPassword")}
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
        />
        <TextField
          type="password"
          size="small"
          fullWidth
          label={t("passwordChange.newPassword")}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <TextField
          type="password"
          size="small"
          fullWidth
          label={t("passwordChange.confirmPassword")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) {
              handleSubmit();
            }
          }}
        />
        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <CircularProgress size={20} />
            ) : (
              t("passwordChange.changeButton")
            )}
          </Button>
        </Box>
      </Box>
    </>
  );
}

export default PasswordChangeSection;
